import type { StytchJWTPayload } from '@/types/stytch'

export type ParsedStytchUser = {
  member_id: string
  email_address: string
  organization_id?: string
  organization_slug?: string
}

export function getOrgSlugFromStytchPayload(
  payload: StytchJWTPayload | null | undefined,
  fallbackToId: boolean = false,
): string | null {
  if (!payload) {
    return null
  }

  const slug = payload['https://stytch.com/organization']?.slug
  if (typeof slug === 'string' && slug.trim().length > 0) {
    return slug.toLowerCase()
  }

  if (!fallbackToId) {
    return null
  }

  const internalOrgId = payload.internal_organization_id
  return typeof internalOrgId === 'string' && internalOrgId.trim().length > 0
    ? internalOrgId.toLowerCase()
    : null
}

/** Comma-separated Stytch authentication factor types from the session JWT (for analytics). */
/**
 * True when any authentication factor includes an impersonated_factor.
 * Use this for both client-decoded and server-validated JWT payloads.
 * Do not infer impersonation from the temporary `stytch_token_type` query param.
 */
export function isImpersonatedStytchPayload(payload: StytchJWTPayload | null | undefined): boolean {
  if (!payload || typeof payload !== 'object') {
    return false
  }

  const factors = payload['https://stytch.com/session']?.authentication_factors
  if (!Array.isArray(factors)) {
    return false
  }

  return factors.some((factor) => {
    if (!factor || typeof factor !== 'object') {
      return false
    }

    return Boolean(factor.impersonated_factor)
  })
}

export function getAuthMethodFromStytchPayload(
  payload: StytchJWTPayload | null | undefined,
): string {
  const factors = payload?.['https://stytch.com/session']?.authentication_factors
  if (!factors?.length) {
    return ''
  }
  return factors.map((f) => f.type).join(', ')
}

export function getSessionEmailFromStytchPayload(
  payload: StytchJWTPayload | null | undefined,
): string | null {
  if (!payload) {
    return null
  }

  const sessionNs = payload['https://stytch.com/session']
  const emailFromFactors =
    sessionNs?.authentication_factors?.reduce((found: string, factor) => {
      if (found) {
        return found
      }

      return (
        factor.email_factor?.email_address ||
        factor.impersonated_factor?.impersonated_email_address ||
        ''
      )
    }, '') || ''

  if (emailFromFactors) {
    return emailFromFactors
  }

  return typeof payload.email === 'string' && payload.email.trim().length > 0 ? payload.email : null
}

export function getSessionMemberInfoFromStytchPayload(
  payload: StytchJWTPayload | null | undefined,
): { internalClientUuid: string | null; userEmail: string | null } {
  if (!payload) {
    return { internalClientUuid: null, userEmail: null }
  }

  return {
    internalClientUuid: payload.internal_organization_id || null,
    userEmail: getSessionEmailFromStytchPayload(payload),
  }
}

export function getStytchUserFromPayload(
  payload: StytchJWTPayload | null | undefined,
): ParsedStytchUser | null {
  if (!payload?.sub) {
    return null
  }

  const email = getSessionEmailFromStytchPayload(payload)
  if (!email) {
    return null
  }

  const orgNs = payload['https://stytch.com/organization']

  return {
    member_id: payload.sub,
    email_address: email,
    organization_id: orgNs?.organization_id || payload.internal_organization_id || undefined,
    organization_slug: orgNs?.slug || undefined,
  }
}
